import { REGEX } from 'constants/common';
import { PaymentMode, UpdateMerchantCustomizationsPayload } from './invoiceSettings.model';
import { PaymentCategory, PaymentCategoryTypes, PaymentSubCategoryTypes, SubCategory } from './type';
import { store } from 'store/store';
import { CommissionBasesSourceType } from './constants';

function organizePaymentModesByCategory(modes: PaymentMode[]): PaymentCategory[] {
  // Initialize a map to group modes by categories
  const categoryMap: { [key: string]: PaymentCategory } = {
    [PaymentCategoryTypes.SELF_CHECKOUT]: {
      displayName: 'Customer self-checkout',
      type: PaymentCategoryTypes.SELF_CHECKOUT,
      paymentModes: [],
      subCategories: []
    },
    [PaymentCategoryTypes.ASSISTED_CHECKOUT]: {
      displayName: 'Assisted checkout',
      type: PaymentCategoryTypes.ASSISTED_CHECKOUT,
      paymentModes: [],
      subCategories: []
    },
    [PaymentCategoryTypes.MANUAL]: {
      displayName: 'Record payments manually',
      type: PaymentCategoryTypes.MANUAL,
      paymentModes: [],
      subCategories: []
    }
  };

  // Process each mode and assign it to the correct category/subCategory
  modes.forEach(mode => {
    const { category, subCategory } = mode;

    // Get the appropriate category object from the map
    const categoryObj = categoryMap[category.type];
    if (categoryObj) {
      if (subCategory) {
        // Handle subCategory logic: find or create the subCategory in the category
        let subCatObj = categoryObj.subCategories?.find(subCat => subCat.type === subCategory.type);

        if (!subCatObj) {
          subCatObj = {
            displayName: subCategory.displayName,
            type: subCategory.type as PaymentSubCategoryTypes,
            paymentModes: []
          };
          categoryObj.subCategories!.push(subCatObj);
        }

        // Add the mode to the subCategory's paymentModes array
        subCatObj.paymentModes.push(mode);
      } else {
        // If no subCategory, add the mode directly to the category's paymentModes array
        categoryObj.paymentModes.push(mode);
      }
    }
  });

  // Now merge any duplicate subCategories
  Object.values(categoryMap).forEach(category => {
    if (category.subCategories) {
      const mergedSubCategories: { [key: string]: SubCategory } = {};

      category.subCategories.forEach(subCategory => {
        // If this subcategory type already exists in the merged map, combine payment modes
        if (mergedSubCategories[subCategory.type]) {
          mergedSubCategories[subCategory.type].paymentModes.push(...subCategory.paymentModes);
        } else {
          // If it's the first time this subcategory type is encountered, add it
          mergedSubCategories[subCategory.type] = subCategory;
        }
      });

      // Replace the subCategories array with the merged version
      category.subCategories = Object.values(mergedSubCategories);
    }
  });

  // Return the categories in the order of PaymentCategoryTypes
  return [
    categoryMap[PaymentCategoryTypes.SELF_CHECKOUT],
    categoryMap[PaymentCategoryTypes.ASSISTED_CHECKOUT],
    categoryMap[PaymentCategoryTypes.MANUAL]
  ];
}

function formatCustomizationPayload(data: UpdateMerchantCustomizationsPayload) {
  const { paymentModes } = data;
  if (paymentModes) {
    const formattedCustomizationData = {
      ...data,
      paymentModes: paymentModes.map((mode: PaymentMode) => ({ id: mode.id, enabled: mode.enabled }))
    };
    return formattedCustomizationData;
  }
  return data;
}

const isValidDecimalInput = (val: string): boolean => {
  return val === '' || REGEX.maximumTwoDecimals.test(val);
};

const cleanNumberInput = (val: string): string => {
  return val.replace(REGEX.nonNumberRegExp, '');
};

const getCommissionBases = ({ type }: { type: CommissionBasesSourceType }) => {
  const { rootReducer } = store.getState();
  const { commissionBases } = rootReducer.app;
  if (type === CommissionBasesSourceType.REFERRAL) {
    return commissionBases.commissionBaseList.filter(item => item.isEnabledForReferrals);
  }
  return commissionBases.commissionBaseList.filter(item => item.isEnabledForCommissions);
};

const getCommissionBaseLabel = (key: string) => {
  if (!key) {
    return '';
  }
  if (key === 'NA') {
    return '-';
  }
  const { rootReducer } = store.getState();
  const { commissionBases } = rootReducer.app;
  return commissionBases.commissionBaseMap[key?.toUpperCase()]?.label || key;
};

export {
  organizePaymentModesByCategory,
  formatCustomizationPayload,
  isValidDecimalInput,
  cleanNumberInput,
  getCommissionBases,
  getCommissionBaseLabel
};
